import HeightIcon from '@mui/icons-material/Height';
import { Dialog, dialogClasses, Slider, Stack } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Case } from '../../../../shared/api/hscan/case';
import { ImageView } from '../../common/components/ImageView';
import {
  CloseButtonContainer,
  ModalCloseButton,
} from '../../common/components/ModalCloseButton';
import { Spacer } from '../../common/components/Spacer';

export const StyledDialog = styled(Dialog)`
  & .${dialogClasses.paper} {
    max-width: 100%;
    max-height: 100%;
  }
`;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  max-width: 750px;
  max-height: 100vh;
  box-sizing: border-box;
  padding: 35px 50px 35px 50px;
  z-index: 1;
`;

export const ModalHeading = styled.span`
  flex-shrink: 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 30px;
  color: ${p => 'var(--HScan_Light_Blue, #2e5be0)' ?? p.theme.colors.black2};
`;

export const ButtonWrapper = styled.div`
  flex-shrink: 0;
  max-width: 100%;
  white-space: nowrap;
  overflow: auto;
`;

export const SeriesButton = styled.button<{
  $selected: boolean;
}>`
  box-sizing: border-box;
  width: 30px;
  height: 35px;
  background-color: ${p => (p.$selected ? '#ffffff' : '#f5f5f5')};
  color: ${p =>
    p.$selected
      ? 'var(--HScan_Light_Blue, #2e5be0)'
      : 'var(--HPACS_Text_Light_Grey, #a3a6ab)'};
  font-size: 14px;
  font-weight: 700;
  text-align: center;
  border: 1px solid var(--Hscan_light_grey, #e7e7e7);
  border-radius: 4px 4px 0 0;
  cursor: pointer;
`;

enum ViewMode {
  FIT_WIDTH,
  FIT_HEIGHT,
}
const ImageViewContainer = styled.div<{ $mode: ViewMode }>`
  overflow: auto;
  line-height: 0;

  ${p =>
    p.$mode === ViewMode.FIT_HEIGHT &&
    `
  display: flex;
  flex-direction: column;

  & img {
    min-height: 0;
    object-fit: contain;
  }
  `}
`;

const ImageModeButtonContainer = styled.div`
  position: relative;
`;

const ImageModeButton_ = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  z-index: 1;

  border: 2px solid #2e5be0;
  outline: 2px solid white;
  border-radius: 6px;
  background: white;
  color: var(--HScan_Light_Blue, #2e5be0);
  line-height: 1;
  opacity: 0.8;

  &:hover {
    opacity: unset;
    cursor: pointer;
  }
`;

const WidthIcon = styled(HeightIcon)`
  transform: rotate(90deg);
`;

interface ImageModeButtonProps {
  mode: ViewMode;
  onClickChangeMode(): void;
}
const ImageModeButton = ({ mode, onClickChangeMode }: ImageModeButtonProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.imageList.imageDetails.maximizeImage',
  });
  const isWidthNextMode = mode === ViewMode.FIT_HEIGHT;

  return (
    <ImageModeButton_
      title={`${isWidthNextMode ? t('fitToWidth') : t('fitToHeight')}`}
      onClick={onClickChangeMode}>
      {isWidthNextMode ? <WidthIcon /> : <HeightIcon />}
    </ImageModeButton_>
  );
};

export const ImageNumberLabel = styled.span`
  color: var(--HPACS_Text_Light_Grey, #a3a6ab);
  font-size: 14px;
  font-weight: 400;
`;

export interface MaxImageModalProps {
  open: boolean;
  onClose: () => void;
  caseInfo: Case;
}
export const MaxImageModal: React.FC<MaxImageModalProps> = (
  props: MaxImageModalProps,
) => {
  const { open, onClose, caseInfo } = props;
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.imageList.imageDetails.maximizeImage',
  });

  const imageIds = caseInfo.series?.length
    ? caseInfo.series.map(series => series.images)
    : [];
  const [seriesIndex, setSeriesIndex] = useState(0);
  const [imageIndex, setImageIndex] = useState(0);

  const onCloseHandler = () => {
    onClose();
    setSeriesIndex(0);
    setImageIndex(0);
  };

  const [viewMode, setViewMode] = useState(ViewMode.FIT_HEIGHT);

  return (
    <StyledDialog onClose={onCloseHandler} open={open}>
      <Container>
        <CloseButtonContainer>
          <ModalCloseButton onClick={onCloseHandler} />
        </CloseButtonContainer>
        <ModalHeading>{t('heading')}</ModalHeading>
        <Spacer height="13px" />
        <ButtonWrapper>
          {imageIds.map((_, index) => (
            <SeriesButton
              key={index}
              $selected={seriesIndex === index}
              onClick={() => {
                setSeriesIndex(index);
                setImageIndex(0);
              }}>
              {index + 1}
            </SeriesButton>
          ))}
        </ButtonWrapper>
        <ImageModeButtonContainer>
          <ImageModeButton
            mode={viewMode}
            onClickChangeMode={() => {
              setViewMode(mode =>
                mode === ViewMode.FIT_HEIGHT
                  ? ViewMode.FIT_WIDTH
                  : ViewMode.FIT_HEIGHT,
              );
            }}
          />
        </ImageModeButtonContainer>
        <ImageViewContainer $mode={viewMode}>
          <ImageView
            width="auto"
            height="auto"
            imageId={imageIds[seriesIndex][imageIndex]}
          />
        </ImageViewContainer>
        <Stack spacing={2} direction="row" alignItems="center">
          <ImageNumberLabel>{1}</ImageNumberLabel>
          <Slider
            value={imageIndex}
            valueLabelDisplay="auto"
            step={1}
            marks
            min={0}
            max={imageIds[seriesIndex].length - 1}
            onChange={(e, value) => setImageIndex(value as number)}
          />
          <ImageNumberLabel>{imageIds[seriesIndex].length}</ImageNumberLabel>
        </Stack>
      </Container>
    </StyledDialog>
  );
};
